// components/PlanSwitcher.tsx
import { useState, useCallback, useMemo, useRef, type FormEvent } from 'react';
import { useAcademicPlans, type CopyType } from '../hooks/useAcademicPlans';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useClickOutside } from '../hooks/useClickOutside';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { PlanClearModal, PlanCopyModal } from './shared/modals';
import type { AcademicPlan } from '../types';
import { controlClasses } from './ui';
import { useCreditUnit } from '../contexts/InstitutionConfigContext';

interface PlanWithNumber extends AcademicPlan {
  planNumber: number;
  future_courses_count?: number;
  total_courses?: number;
  total_credits_planned?: number;
  [key: string]: unknown;
}

interface CopyModalData {
  fromPlan: PlanWithNumber;
  toPlan: PlanWithNumber;
}

export interface PlanSwitcherProps {
  onPlanChange?: (planId: number) => void | Promise<void>;
  onRefreshPlan?: () => Promise<void>;
}

export const PlanSwitcher = ({ onPlanChange, onRefreshPlan }: PlanSwitcherProps = {}) => {
  const creditUnit = useCreditUnit();
  const { user } = useAuth();
  const { plans, loading, setActivePlan, clearPlan, copyPlan, renamePlan } = useAcademicPlans();
  const { showError } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [operationLoading, setOperationLoading] = useState<string | null>(null);
  const [copyMenuPlanId, setCopyMenuPlanId] = useState<number | null>(null);
  const [clearModalPlan, setClearModalPlan] = useState<PlanWithNumber | null>(null);
  const [copyModalData, setCopyModalData] = useState<CopyModalData | null>(null);

  const plansWithNumbers = useMemo<PlanWithNumber[]>(
    () =>
      plans.map((plan, index) => ({
        ...(plan as AcademicPlan & {
          future_courses_count?: number;
          total_courses?: number;
          total_credits_planned?: number;
        }),
        planNumber: index + 1,
      })),
    [plans]
  );

  const formatPlanName = useCallback((plan: PlanWithNumber): string => {
    const baseName = `Plan ${plan.planNumber}`;
    if (plan.plan_name && !plan.plan_name.match(/^Plan \d+$/)) {
      return `${baseName} · ${plan.plan_name}`;
    }
    return baseName;
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleCloseDropdown = useCallback(() => {
    setIsOpen(false);
    setEditingPlanId(null);
    setCopyMenuPlanId(null);
  }, []);

  // Panel dismissal: outside click + Escape, only while open.
  useClickOutside(containerRef, handleCloseDropdown, isOpen);
  useEscapeKey(handleCloseDropdown, isOpen);

  const handleSetActive = useCallback(
    async (planId: number) => {
      setOperationLoading(`active-${planId}`);
      try {
        await setActivePlan(planId);
        setIsOpen(false);
        if (onPlanChange) {
          onPlanChange(planId);
        }
      } catch (error) {
        console.error('Failed to set active plan:', error);
        showError('Failed to switch plan. Please try again.');
      } finally {
        setOperationLoading(null);
      }
    },
    [setActivePlan, onPlanChange, showError]
  );

  const handleRenamePlan = useCallback(
    async (planId: number) => {
      setOperationLoading(`rename-${planId}`);
      try {
        await renamePlan(planId, editingName);
        setEditingPlanId(null);
        setEditingName('');
      } catch (error) {
        console.error('Failed to rename plan:', error);
        showError('Failed to rename plan. Please try again.');
      } finally {
        setOperationLoading(null);
      }
    },
    [renamePlan, editingName, showError]
  );

  const handleClearPlan = useCallback(async () => {
    if (!clearModalPlan) return;

    setOperationLoading(`clear-${clearModalPlan.id}`);
    try {
      await clearPlan(clearModalPlan.id);
      if (onPlanChange) {
        onPlanChange(clearModalPlan.id);
      }
      if (onRefreshPlan) {
        await onRefreshPlan();
      }
    } finally {
      setOperationLoading(null);
    }
  }, [clearModalPlan, clearPlan, onPlanChange, onRefreshPlan]);

  const handleCopyPlan = useCallback(
    async (copyType: CopyType) => {
      if (!copyModalData) return;

      setOperationLoading(`copy-${copyModalData.toPlan.id}`);
      try {
        await copyPlan(copyModalData.fromPlan.id, copyModalData.toPlan.id, copyType);
        if (onPlanChange) {
          onPlanChange(copyModalData.toPlan.id);
        }
        if (onRefreshPlan) {
          await onRefreshPlan();
        }
      } finally {
        setOperationLoading(null);
      }
    },
    [copyModalData, copyPlan, onPlanChange, onRefreshPlan]
  );

  const openCopyModal = useCallback((fromPlan: PlanWithNumber, toPlan: PlanWithNumber) => {
    setCopyModalData({ fromPlan, toPlan });
    setCopyMenuPlanId(null);
  }, []);

  const handleCloseClearModal = useCallback(() => setClearModalPlan(null), []);
  const handleCloseCopyModal = useCallback(() => setCopyModalData(null), []);

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
        <span>Loading plans...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-sm text-gray-500">
        Sign in to save and manage multiple plans
      </div>
    );
  }

  const activePlanWithNumber = plansWithNumbers.find((p) => p.is_active) || plansWithNumbers[0];

  const triggerButton = (
    <button
      onClick={() => setIsOpen(!isOpen)}
      aria-expanded={isOpen}
      aria-haspopup="true"
      // Height/radius/transition come from the control contract (ui/control.ts),
      // shared with SchoolSwitcher, the Feedback pill and the avatar trigger so
      // the header row sits on one baseline. Tokens rather than gray-*: the old
      // bg-white was white in dark mode too.
      className={controlClasses(
        'md',
        'gap-2 px-3 text-sm bg-surface-card text-ink border border-line-soft',
        'hover:bg-surface-sunken focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
      )}
    >
      <span className="font-medium">
        {activePlanWithNumber ? formatPlanName(activePlanWithNumber) : 'Select Plan'}
      </span>
      <svg
        className={`h-4 w-4 text-ink-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <>
      {/* Panel shell: this dropdown holds arbitrary interactive content (an
          inline rename input, a nested copy submenu, per-row icon buttons), so
          it is not a `ui/DropdownMenu` — that Radix menu primitive runs
          typeahead on every character key and would steal focus from the
          rename input. */}
      <div className="relative" ref={containerRef}>
        {triggerButton}

        {isOpen && (
        <div
          role="menu"
          className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
        >
        <div className="p-2">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-2">
            Your Plans
          </div>

          {plansWithNumbers.length === 0 ? (
            <div className="px-3 py-4 text-center text-gray-500">
              <p className="text-sm">No plans yet</p>
              <p className="text-xs mt-1">Plans will be created automatically</p>
            </div>
          ) : (
            plansWithNumbers.map((plan) => {
              const courseCount = plan.future_courses_count || plan.total_courses || 0;
              const isEmpty = courseCount === 0;
              const otherPlans = plansWithNumbers.filter((p) => p.id !== plan.id);

              return (
                <div
                  key={plan.id}
                  className={`group relative px-3 py-2 rounded-md hover:bg-gray-50 ${
                    plan.is_active ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}
                >
                  {editingPlanId === plan.id ? (
                    <form
                      onSubmit={(e: FormEvent<HTMLFormElement>) => {
                        e.preventDefault();
                        handleRenamePlan(plan.id);
                      }}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                        disabled={operationLoading === `rename-${plan.id}`}
                      />
                      <button
                        type="submit"
                        className="text-green-600 hover:text-green-700 disabled:opacity-50"
                        disabled={operationLoading === `rename-${plan.id}`}
                      >
                        {operationLoading === `rename-${plan.id}` ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        ) : (
                          '✓'
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingPlanId(null);
                          setEditingName('');
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </form>
                  ) : (
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => handleSetActive(plan.id)}
                        className="flex-1 text-left"
                        disabled={operationLoading === `active-${plan.id}`}
                      >
                        <div className="flex items-center">
                          <span className="font-medium text-gray-900 break-words">
                            {formatPlanName(plan)}
                          </span>
                          {isEmpty && (
                            <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">
                              empty
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {creditUnit.format(plan.total_credits_planned || 0)} ·
                          {courseCount} courses
                          {plan.majors?.length > 0 && (
                            <>
                              {' '}
                              ·{' '}
                              {typeof plan.majors[0] === 'object'
                                ? (plan.majors[0] as { program?: string; code?: string }).program
                                : plan.majors[0]}
                            </>
                          )}
                        </div>
                      </button>

                      {operationLoading === `active-${plan.id}` && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      )}

                      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => {
                            setEditingPlanId(plan.id);
                            setEditingName(plan.plan_name);
                            setCopyMenuPlanId(null);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Rename plan"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                        </button>

                        <div className="relative">
                          <button
                            onClick={() => setCopyMenuPlanId(copyMenuPlanId === plan.id ? null : plan.id)}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Copy from another plan"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          </button>

                          {copyMenuPlanId === plan.id && otherPlans.length > 0 && (
                            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20">
                              <div className="py-1">
                                <div className="px-3 py-1 text-xs font-medium text-gray-500">
                                  Copy from:
                                </div>
                                {otherPlans.map((otherPlan) => (
                                  <button
                                    key={otherPlan.id}
                                    onClick={() => openCopyModal(otherPlan, plan)}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                  >
                                    {formatPlanName(otherPlan)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => {
                            setClearModalPlan(plan);
                            setCopyMenuPlanId(null);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Clear all courses from plan"
                          disabled={operationLoading === `clear-${plan.id}` || isEmpty}
                        >
                          {operationLoading === `clear-${plan.id}` ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}

          <div className="border-t border-gray-200 mt-2 pt-2 px-3 py-2">
            <p className="text-xs text-gray-500">
              Use the copy button to duplicate content between plans
            </p>
          </div>
        </div>
        </div>
        )}
      </div>

      <PlanClearModal
        isOpen={!!clearModalPlan}
        onClose={handleCloseClearModal}
        onConfirm={handleClearPlan}
        plan={clearModalPlan}
        isLoading={clearModalPlan ? operationLoading === `clear-${clearModalPlan.id}` : false}
      />

      <PlanCopyModal
        isOpen={!!copyModalData}
        onClose={handleCloseCopyModal}
        onConfirm={handleCopyPlan}
        fromPlan={copyModalData?.fromPlan}
        toPlan={copyModalData?.toPlan}
        isLoading={copyModalData ? operationLoading === `copy-${copyModalData.toPlan.id}` : false}
      />
    </>
  );
};
